import { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios'

import { Api } from 'common/api/api'
import { apiConfig } from 'common/api/api.config'
import { HomeItem, HomeTab, ITabs } from 'common/types/home'
import { GenericBooleanResponse } from 'common/types/managers'
import {
  AppAdditionalInfo,
  AppInstanceInfo,
  AppProperties,
  DirectAccessMetadata,
  DirectAccessResolution,
  ExampleItem,
  OpenAppResponse,
  OpenAppStatus,
  ShowVersionsOnOpenData
} from 'common/types/application'
import {
  AppVersionsNamesData,
  AppVersionStatus,
  CreateAppVersionParams,
  ExportAppVersionsProps,
  GetAppVersionsParams,
  UpdateAppVersionParams
} from 'common/types/version'
import { MyDepartmentTeam } from 'common/types/team'
import { UserInstanceData } from 'common/types/instance'

const ENDPOINT = '/apps'

export class ApplicationApi extends Api {
  public constructor(config: AxiosRequestConfig) {
    super(config)

    // this middleware is been called right before the http request is made.
    this.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param
    }))

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }))

    Object.setPrototypeOf(this, ApplicationApi.prototype)
    this.getAppStatus = this.getAppStatus.bind(this)
    this.saveApp = this.saveApp.bind(this)
    this.saveAppInMyWorkspace = this.saveAppInMyWorkspace.bind(this)
    this.saveAppInMyTeam = this.saveAppInMyTeam.bind(this)
    this.openApp = this.openApp.bind(this)
    this.getOpenStatus = this.getOpenStatus.bind(this)
    this.closeApp = this.closeApp.bind(this)
    this.cancelOpenApp = this.cancelOpenApp.bind(this)
    this.createApp = this.createApp.bind(this)
    this.getAppProperties = this.getAppProperties.bind(this)
    this.setAppProperties = this.setAppProperties.bind(this)
    this.getDefaultInterface = this.getDefaultInterface.bind(this)
    this.reloadWithMyVenv = this.reloadWithMyVenv.bind(this)
    this.getRequirementsFile = this.getRequirementsFile.bind(this)
    this.replaceAndInstallRequirementsFile = this.replaceAndInstallRequirementsFile.bind(this)
    this.getExamples = this.getExamples.bind(this)
    this.downloadExample = this.downloadExample.bind(this)
    this.getClosedAppVersions = this.getClosedAppVersions.bind(this)
    this.searchApps = this.searchApps.bind(this)
    this.createVersionFromClosedApp = this.createVersionFromClosedApp.bind(this)
    this.versionExistsInClosedApp = this.versionExistsInClosedApp.bind(this)
    this.updateClosedAppVersionsStatus = this.updateClosedAppVersionsStatus.bind(this)
    this.updateClosedAppVersionProperties = this.updateClosedAppVersionProperties.bind(this)
    this.deleteClosedAppVersions = this.deleteClosedAppVersions.bind(this)
    this.exportClosedAppVersions = this.exportClosedAppVersions.bind(this)
    this.importClosedAppVersions = this.importClosedAppVersions.bind(this)
    this.setClosedAppDefaultVersion = this.setClosedAppDefaultVersion.bind(this)
    this.createDirectAccessLink = this.createDirectAccessLink.bind(this)
    this.resolveDirectAccessLink = this.resolveDirectAccessLink.bind(this)
  }

  public async getHome(): Promise<ITabs> {
    try {
      const res: AxiosResponse<ITabs> = await this.get(`${ENDPOINT}/getHome/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getInitialAppsAndTabs(fromHome?: boolean): Promise<ITabs> {
    try {
      const res: AxiosResponse<ITabs> = await this.get(`${ENDPOINT}/getInitialAppsAndTabs/`, {
        params: { fromHome }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getAppsAndFolders(
    folder: string,
    tabName: string,
    showHidden: boolean
  ): Promise<HomeTab> {
    try {
      const res: AxiosResponse<HomeTab> = await this.get(`${ENDPOINT}/getAppsAndFolders/`, {
        params: { folder, tabName, showHidden }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async createNewAppFolder(
    folderName: string,
    currentPath: string,
    tabName: string
  ): Promise<HomeTab> {
    try {
      const res: AxiosResponse<HomeTab> = await this.post(`${ENDPOINT}/createNewAppFolder/`, {
        folderName,
        currentPath,
        tabName
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async checkAppFolderExists(folder: string, tabName: string): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.get(`${ENDPOINT}/checkAppFolderExists/`, {
        params: { folder, tabName }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async openApp(
    filePath: string,
    openAsReadOnly?: boolean,
    version?: string,
    resources?: string,
    forceNewInstance?: boolean,
    attemptRecovery?: boolean
  ): Promise<OpenAppResponse> {
    try {
      const data = {
        folder: filePath,
        openAsReadOnly,
        version,
        resources,
        forceNewInstance,
        attemptRecovery
      }
      const res: AxiosResponse<OpenAppResponse> = await this.post(`${ENDPOINT}/open/`, data)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getOpenStatus(folder: string, version?: string): Promise<OpenAppStatus> {
    try {
      const res: AxiosResponse<OpenAppStatus> = await this.get(`${ENDPOINT}/openStatus/`, {
        params: { folder, version }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async closeApp(): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.get(`${ENDPOINT}/close/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async cancelOpenApp(): Promise<[true, UserInstanceData]> {
    try {
      const res: AxiosResponse<UserInstanceData> = await this.post(`${ENDPOINT}/cancelOpenApp/`)
      return [true, this.success(res) as UserInstanceData]
    } catch (error) {
      throw error
    }
  }
  /**
   * Get Model unsaved changes status
   * @params
   * @returns boolean for hasUnsavedChanges (true =  unsavedChanges)
   */
  public async getAppStatus(): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.get(`${ENDPOINT}/status/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async saveApp(): Promise<AppInstanceInfo> {
    try {
      const res: AxiosResponse<AppInstanceInfo> = await this.get(`${ENDPOINT}/save/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async claimWriteMode(): Promise<AppInstanceInfo> {
    try {
      const res: AxiosResponse<AppInstanceInfo> = await this.post(`${ENDPOINT}/claimWriteMode/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async saveAppInMyWorkspace(
    folderName: string,
    versions?: string[]
  ): Promise<AppInstanceInfo> {
    try {
      const res: AxiosResponse<AppInstanceInfo> = await this.post(
        `${ENDPOINT}/saveAppInMyWorkspace/`,
        {
          folder_name: folderName,
          versions
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async saveAppInMyTeam(
    folderName: string,
    teamFolder: string,
    versions?: string[]
  ): Promise<AppInstanceInfo> {
    try {
      const res: AxiosResponse<AppInstanceInfo> = await this.post(`${ENDPOINT}/saveAppInMyTeam/`, {
        folderName: folderName,
        teamFolder: teamFolder,
        versions
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async uploadThumbnail(data: FormData, token: CancelToken): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.post(`${ENDPOINT}/uploadThumbnail/`, data, {
        cancelToken: token
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async removeThumbnail(path: String): Promise<boolean> {
    try {
      const data = { uri: path }
      const res: AxiosResponse<any> = await this.delete(`${ENDPOINT}/removeThumbnail/`, {
        data: data
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async changeToOtherInstance(instanceId: string): Promise<AppInstanceInfo> {
    const query = `?new_instance_id=${instanceId}`
    const res: AxiosResponse<AppInstanceInfo> = await this.get(
      `${ENDPOINT}/changeToOtherInstance/${query}`
    )
    return this.success(res)
  }

  public async createApp(
    name: string,
    template: string,
    onPublic: boolean,
    pythonVersion: string,
    currentTeamTab: MyDepartmentTeam | null,
    commonTabName: string,
    currentAppFolder: string
  ): Promise<HomeItem> {
    try {
      const res: AxiosResponse<HomeItem> = await this.post(`${ENDPOINT}/create/`, {
        name,
        template,
        onPublic,
        pythonVersion,
        currentTeamTab,
        commonTabName,
        currentAppFolder
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async importApp(data: FormData, token: CancelToken): Promise<any> {
    const res: AxiosResponse<any> = await this.post(`${ENDPOINT}/importApp/`, data, {
      cancelToken: token
    })
    return this.success(res)
  }
  public async deleteApp(path: String): Promise<any> {
    try {
      const data = { uri: path }
      const res: AxiosResponse<any> = await this.delete(`${ENDPOINT}/delete/`, {
        data: data
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async renameApp(uri: string, name: string, fromFolder: boolean): Promise<any> {
    try {
      const res: AxiosResponse<any> = await this.put(`${ENDPOINT}/rename/`, {
        uri,
        name,
        fromFolder
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getAppProperties(): Promise<AppProperties> {
    try {
      const res: AxiosResponse<AppProperties> = await this.get(`${ENDPOINT}/getAppProperties/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getAppAdditionalInfo(): Promise<AppAdditionalInfo> {
    try {
      const res: AxiosResponse<AppAdditionalInfo> = await this.get(
        `${ENDPOINT}/getAppAdditionalInfo/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async setAppProperties(appProperties: AppProperties): Promise<AppInstanceInfo> {
    try {
      const res: AxiosResponse<AppInstanceInfo> = await this.post(
        `${ENDPOINT}/setAppProperties/`,
        appProperties
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getDefaultInterface(): Promise<{ id: string; name: string } | undefined> {
    try {
      const res: AxiosResponse<{ id: string; name: string } | undefined> = await this.get(
        `${ENDPOINT}/getDefaultInterface/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async reloadWithMyVenv(): Promise<AppInstanceInfo> {
    const res: AxiosResponse<AppInstanceInfo> = await this.get(`${ENDPOINT}/reloadWithMyVenv/`)
    return this.success(res)
  }
  public async createAppFromThis(
    name: string,
    source: string,
    keepId?: boolean
  ): Promise<{ name: string; source: string }> {
    try {
      const data = { name: name.trim(), source: source.trim(), keepId }
      const res: AxiosResponse<any> = await this.post(`${ENDPOINT}/createAppFromThis/`, data)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getRequirementsFile(): Promise<string> {
    try {
      const res: AxiosResponse<string> = await this.get(`${ENDPOINT}/getRequirementsFile/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async replaceAndInstallRequirementsFile(value: string): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(
        `${ENDPOINT}/replaceAndInstallRequirementsFile/`,
        value
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getExamples(): Promise<ExampleItem[]> {
    try {
      const res: AxiosResponse<ExampleItem[]> = await this.get(`${ENDPOINT}/getExamples/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async downloadExample(name: string): Promise<HomeItem> {
    try {
      const res: AxiosResponse<HomeItem> = await this.post(`${ENDPOINT}/downloadExample/`, {
        name
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async showVersionsOnOpen(folder: string): Promise<ShowVersionsOnOpenData> {
    try {
      const data = { folder }
      const res: AxiosResponse<ShowVersionsOnOpenData> = await this.post(
        `${ENDPOINT}/showVersionsOnOpen/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getClosedAppVersions(data: GetAppVersionsParams): Promise<AppVersionsNamesData> {
    try {
      const res: AxiosResponse<AppVersionsNamesData> = await this.post(
        `${ENDPOINT}/getClosedAppVersions/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async searchApps(text: string, currentTab: string, filterBy: string): Promise<HomeTab> {
    try {
      const res: AxiosResponse<HomeTab> = await this.get(`${ENDPOINT}/searchApps/`, {
        params: { text, currentTab, filterBy }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async createVersionFromClosedApp(
    folder: string,
    data: CreateAppVersionParams
  ): Promise<AppInstanceInfo> {
    try {
      const allData = { folder, ...data }
      const res: AxiosResponse<AppInstanceInfo> = await this.post(
        `${ENDPOINT}/createVersionFromClosedApp/`,
        allData
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async versionExistsInClosedApp(
    folder: string,
    versionName: string,
    canWriteInDir: boolean
  ): Promise<GenericBooleanResponse> {
    try {
      const data = { folder, version_name: versionName, can_write_in_dir: canWriteInDir }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/versionExistsInClosedApp/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async updateClosedAppVersionsStatus(
    folder: string,
    versions: string[],
    newStatus: AppVersionStatus
  ): Promise<GenericBooleanResponse> {
    try {
      const data = { folder, versions, new_status: newStatus }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/updateClosedAppVersionsStatus/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async updateClosedAppVersionProperties(
    folder: string,
    version: string,
    newProperties: UpdateAppVersionParams
  ): Promise<GenericBooleanResponse> {
    try {
      const data = { folder, version, new_properties: newProperties }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/updateClosedAppVersionProperties/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async deleteClosedAppVersions(
    folder: string,
    versions: string[]
  ): Promise<GenericBooleanResponse> {
    try {
      const data = { folder, versions }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/deleteClosedAppVersions/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async exportClosedAppVersions(
    folder: string,
    versions: string[]
  ): Promise<ExportAppVersionsProps<string>> {
    try {
      const data = { folder, versions_names: versions }
      const res: AxiosResponse<ExportAppVersionsProps<string>> = await this.post(
        `${ENDPOINT}/exportClosedAppVersions/`,
        data,
        {
          responseType: 'blob'
        }
      )
      return { data: this.success<any>(res), headers: res.headers }
    } catch (error) {
      throw error
    }
  }
  public async importClosedAppVersions(
    folder: string,
    zipFilePath: string
  ): Promise<GenericBooleanResponse> {
    try {
      const data = { folder, zip_file_path: zipFilePath }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/importClosedAppVersions/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async setClosedAppDefaultVersion(
    folder: string,
    version: string
  ): Promise<GenericBooleanResponse> {
    try {
      const data = { folder, version }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/setClosedAppDefaultVersion/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async createDirectAccessLink(
    folder: string,
    companyCode: string,
    version?: string,
    openAsReadOnly: boolean = false
  ): Promise<{
    companyCode: string
    appSlug: string
    folder: string
    version?: string | null
    openAsReadOnly: boolean
  }> {
    try {
      const res: AxiosResponse<{
        companyCode: string
        appSlug: string
        folder: string
        version?: string | null
        openAsReadOnly: boolean
      }> = await this.post(`${ENDPOINT}/createDirectAccessLink/`, {
        folder,
        companyCode,
        version,
        openAsReadOnly
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async resolveDirectAccessLink(params: {
    companyCode: string
    folder: string
    version?: string
    openAsReadOnly?: boolean
    metadataOnly?: boolean
  }): Promise<DirectAccessMetadata | DirectAccessResolution> {
    try {
      const res: AxiosResponse<DirectAccessMetadata | DirectAccessResolution> = await this.get(
        `${ENDPOINT}/resolveDirectAccessLink/`,
        {
          params: {
            companyCode: params.companyCode,
            folder: params.folder,
            version: params.version,
            openAsReadOnly: params.openAsReadOnly === undefined ? false : params.openAsReadOnly,
            metadataOnly: params.metadataOnly === true
          }
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async removeFolders(folders: string[]): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(`${ENDPOINT}/removeFolders/`, { folders })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
}

export const applicationApi = new ApplicationApi(apiConfig)
